import { TouchEvent as ReactTouchEvent, MouseEvent as ReactMouseEvent, useRef } from "react";

// Swipe-left-to-clear gesture for notification rows on touch devices.
//
// Design constraints (project motion conventions):
//   - Compositor-only: we drive the foreground via `transform: translate3d` and
//     the revealed backdrop via `opacity` — never width/height/left.
//   - No scroll-linked motion: the gesture reads touch deltas directly; the host
//     row sets `touch-action: pan-y` so vertical panning still scrolls the feed
//     while horizontal drags are handed to JS (no preventDefault needed, which
//     also sidesteps React's passive-listener limitation).
//   - One-shot `will-change`: promoted on touchstart, cleared on settle/commit.
//   - Reduced motion: the commit/cancel transitions are skipped (instant).
//
// Everything is driven imperatively off refs so a 60fps drag never triggers a
// React re-render — only the terminal commit calls back into React (to dismiss).

// Match NotificationBell's module-level probe rather than re-querying per render.
const prefersReducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Commit when the row is dragged at least this fraction of its width…
const COMMIT_FRACTION = 0.4;
// …but never require more than feels natural on a wide row, nor less than this.
const MIN_COMMIT_PX = 72;
// Ignore micro-movements before deciding the gesture is a horizontal swipe.
const DIRECTION_LOCK_PX = 8;
// A quick left flick commits even below the distance threshold.
const FLICK_VELOCITY = 0.5; // px per ms
const FLICK_MIN_PX = 40;

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

interface Params {
  /** Gate the whole gesture (desktop keeps the hover × button instead). */
  enabled: boolean;
  /** Run once the swipe-left passes the commit threshold. */
  onCommit: () => void;
}

export const useSwipeToDismiss = ({ enabled, onCommit }: Params) => {
  // The translated foreground layer and the colour backdrop revealed beneath it.
  const ref = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const startX = useRef(0);
  const startY = useRef(0);
  const startT = useRef(0);
  const lock = useRef<null | "horizontal" | "vertical">(null);
  const dx = useRef(0);
  const width = useRef(0);
  const committed = useRef(false);
  // Set once a horizontal drag is underway so the trailing click is swallowed.
  const swiped = useRef(false);

  const commitDistance = () => Math.max(MIN_COMMIT_PX, width.current * COMMIT_FRACTION);

  const paint = (offset: number) => {
    const el = ref.current;
    if (el) el.style.transform = `translate3d(${offset}px, 0, 0)`;
    const backdrop = backdropRef.current;
    if (backdrop) {
      backdrop.style.opacity = String(Math.min(1, Math.abs(offset) / commitDistance()));
    }
  };

  const settle = (animate: boolean) => {
    const el = ref.current;
    const backdrop = backdropRef.current;
    if (!el) return;
    const animated = animate && !prefersReducedMotion;
    el.style.transition = animated ? `transform 240ms ${EASE}` : "none";
    el.style.transform = "translate3d(0, 0, 0)";
    if (backdrop) {
      backdrop.style.transition = animated ? "opacity 200ms ease" : "none";
      backdrop.style.opacity = "0";
    }
    const clearPromotion = () => {
      el.style.willChange = "auto";
      if (backdrop) backdrop.style.willChange = "auto";
    };
    if (animated) {
      const clear = () => {
        clearPromotion();
        el.removeEventListener("transitionend", clear);
      };
      el.addEventListener("transitionend", clear);
    } else {
      clearPromotion();
    }
  };

  const onTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!enabled || e.touches.length !== 1) return;
    const el = ref.current;
    width.current = el?.clientWidth || window.innerWidth;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    startT.current = Date.now();
    lock.current = null;
    dx.current = 0;
    committed.current = false;
    swiped.current = false;
    if (el) {
      el.style.transition = "none";
      el.style.willChange = "transform";
    }
    if (backdropRef.current) {
      backdropRef.current.style.transition = "none";
      backdropRef.current.style.willChange = "opacity";
    }
  };

  const onTouchMove = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!enabled || committed.current) return;
    const moveX = e.touches[0].clientX - startX.current;
    const moveY = e.touches[0].clientY - startY.current;

    // Lock the gesture's axis once it clears the dead zone. A vertical lock bows
    // out for the rest of the gesture so the feed scrolls untouched.
    if (lock.current === null) {
      if (Math.abs(moveX) < DIRECTION_LOCK_PX && Math.abs(moveY) < DIRECTION_LOCK_PX) return;
      lock.current = Math.abs(moveX) > Math.abs(moveY) ? "horizontal" : "vertical";
    }
    if (lock.current !== "horizontal") return;

    // Left-only: a rightward pull meets rubber-band resistance and is capped.
    let offset = moveX;
    if (offset > 0) offset = Math.min(offset * 0.25, 24);
    dx.current = offset;
    if (Math.abs(offset) > DIRECTION_LOCK_PX) swiped.current = true;
    paint(offset);
  };

  const onTouchEnd = () => {
    if (!enabled || lock.current !== "horizontal") {
      settle(false);
      return;
    }
    const velocity = dx.current / Math.max(1, Date.now() - startT.current);
    const farEnough = -dx.current >= commitDistance();
    const flick = velocity <= -FLICK_VELOCITY && -dx.current >= FLICK_MIN_PX;

    if (!farEnough && !flick) {
      settle(true);
      return;
    }

    committed.current = true;
    const el = ref.current;
    const backdrop = backdropRef.current;
    if (prefersReducedMotion || !el) {
      onCommit();
      return;
    }

    // Fling the row off the leading (left) edge, fade it out, hold the backdrop
    // fully lit, then hand off to React to remove it from the feed.
    el.style.transition = `transform 220ms ${EASE}, opacity 200ms linear`;
    el.style.transform = `translate3d(-${width.current}px, 0, 0)`;
    el.style.opacity = "0";
    if (backdrop) {
      backdrop.style.transition = "opacity 120ms ease";
      backdrop.style.opacity = "1";
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener("transitionend", finish);
      onCommit();
    };
    el.addEventListener("transitionend", finish);
    // Fallback in case transitionend never fires (interrupted / display change).
    window.setTimeout(finish, 320);
  };

  const onTouchCancel = () => {
    if (!enabled || committed.current) return;
    settle(true);
  };

  // Capture phase: a tap that was actually a horizontal swipe must not also
  // trigger the row's navigate/mark-read click.
  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (swiped.current) {
      e.preventDefault();
      e.stopPropagation();
      swiped.current = false;
    }
  };

  return {
    ref,
    backdropRef,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onClickCapture },
  };
};

export default useSwipeToDismiss;
